import { useState } from 'react';
import { Link } from 'react-router';
import { ChevronRight, Loader2, MessageSquare, Users, Plus } from 'lucide-react';
import { useWhatsAppGroups } from '../hooks/useWhatsAppGroups';
import type { WhatsAppGroup } from '@lms/shared/types';
import { Button } from '@lms/ui';
import { WhatsAppGroupModal } from '../components/WhatsAppGroupModal';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function WhatsAppGroups() {
  const { data: groups, isLoading, error } = useWhatsAppGroups();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { language } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.whatsAppGroups.loadingGroups} />
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-xl">
        <h3 className="font-headline font-bold mb-2">
          <Bilingual {...copy.whatsAppGroups.errorTitle} />
        </h3>
        <p className="text-sm font-body">{(error as Error).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-12 animate-fadeIn">
      {/* Header */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-10 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <h1 className="text-4xl font-headline font-black text-on-surface tracking-tight leading-none m-0">
            <Bilingual {...copy.whatsAppGroups.listTitle} />
          </h1>
          <p className="text-lg font-body text-on-surface-variant max-w-2xl">
            <Bilingual {...copy.whatsAppGroups.listSubtitle} />
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="h-12 px-8 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest shadow-level1"
        >
          <Plus size={18} />
          <Bilingual {...copy.whatsAppGroups.btnAddGroup} />
        </Button>
      </section>

      {isModalOpen && <WhatsAppGroupModal onClose={() => setIsModalOpen(false)} />}

      {/* Grid of Groups */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {((groups as WhatsAppGroup[]) || []).map(group => (
          <div
            key={group.id}
            className="bg-surface-container-lowest p-6 rounded-2xl shadow-level1 border border-on-surface/5 group hover:border-primary/20 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-primary/5 text-primary rounded-xl group-hover:bg-primary group-hover:text-on-primary transition-all">
                <MessageSquare size={24} />
              </div>
              <div className="flex gap-2">
                <span
                  className={`text-[10px] font-label font-bold px-2 py-1 rounded uppercase tracking-wider ${
                    group.gender === 'IKHWAN'
                      ? 'bg-blue-100 text-blue-700'
                      : group.gender === 'AKHWAT'
                        ? 'bg-pink-100 text-pink-700'
                        : 'bg-surface-container-high text-on-surface-variant'
                  }`}
                >
                  {group.gender}
                </span>
              </div>
            </div>

            <h3 className="text-lg font-headline font-bold text-on-surface m-0 mb-1 group-hover:text-primary transition-colors">
              {group.name}
            </h3>
            <p className="text-sm font-body text-on-surface-variant line-clamp-2 mb-6">
              {group.description ||
                (language === 'ar'
                  ? copy.whatsAppGroups.noDescription.ar
                  : copy.whatsAppGroups.noDescription.id)}
            </p>

            <div className="flex items-center justify-between pt-6 border-t border-on-surface/5">
              <div className="flex items-center gap-2 text-on-surface-variant">
                <Users size={16} />
                <span className="text-sm font-label">
                  {group._count?.members || 0} / {group.maxMembers}
                </span>
              </div>
              <Link
                to={`/whatsapp-groups/${group.id}`}
                className="flex items-center gap-1 text-xs font-label font-bold text-primary hover:gap-2 transition-all"
              >
                <Bilingual {...copy.whatsAppGroups.inspect} />
                <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>

      {(!groups || groups.length === 0) && (
        <div className="bg-surface-container-low rounded-3xl p-20 text-center border-2 border-dashed border-outline-variant/20">
          <MessageSquare className="w-12 h-12 text-on-surface-variant/20 mx-auto mb-4" />
          <h3 className="text-xl font-headline font-bold text-on-surface m-0">
            <Bilingual {...copy.whatsAppGroups.emptyTitle} />
          </h3>
          <p className="text-on-surface-variant font-body">
            <Bilingual {...copy.whatsAppGroups.emptyState} />
          </p>
        </div>
      )}
    </div>
  );
}
